import type { InputType } from 'config'
import type { ISelectOption } from 'stories/components/SelectOptionInputs/types'

export type ServicingInfoTab = 'Loan Details' | 'Flood & Appraisal' | 'Insurance' | 'Taxes'

export const SERVICING_INFO_TABS: ServicingInfoTab[] = [
  'Loan Details',
  'Flood & Appraisal',
  'Insurance',
  'Taxes',
]

/** Older configs stored custom sections on the removed Default & Extensions tab. */
export const normalizeServicingInfoTab = (tab: string | undefined): ServicingInfoTab =>
  SERVICING_INFO_TABS.includes(tab as ServicingInfoTab) ? (tab as ServicingInfoTab) : 'Loan Details'

/**
 * Hardcoded section keys bucketed by tab. Anything not listed here falls into
 * 'Loan Details' (origination, core loan terms, additional info, reminders).
 * These must stay in sync with the runtime render in ServicingNote.tsx.
 */
export const INSURANCE_TAB_SECTION_KEYS = ['insuranceSection', 'insuranceReminderSection']
export const TAXES_TAB_SECTION_KEYS = ['taxSection', 'taxReminderSection']
export const PROPERTY_TAB_SECTION_KEYS = ['appraisalSection', 'floodSection']

export interface IServicingInfoFieldConfig {
  title?: string
  visible?: boolean
  required?: boolean
}

export interface IServicingInfoSectionConfig {
  title?: string
}

/**
 * Stored `inputType` for custom servicing fields.
 *
 * Mirrors a subset of `NewInputTypeOptions` from `config/loan.input.visibility.type`.
 * `customFieldToInput` normalizes these into the lowercase runtime variants
 * that `RenderInput` actually consumes.
 */
export type CustomFieldInputType = 'Text' | 'TextArea' | 'Select' | 'CheckBox' | 'ToggleButton' | 'FileTable'

/**
 * Input-component types the admin picker offers when creating a new custom
 * field. Order here drives the dropdown order.
 */
export const ServicingCustomFieldInputTypeOptions: Record<CustomFieldInputType, string> = {
  Text: 'Text',
  ToggleButton: 'Toggle Button',
  Select: 'Select',
  TextArea: 'Text Area',
  CheckBox: 'Checkbox',
  FileTable: 'File Table',
}

/**
 * For `inputType: 'Text'`, the `type` controls the format (date, dollar, etc.).
 * Mirrors `NewInputTextOptions` keys.
 */
export type CustomFieldTextFormat =
  | 'text'
  | 'number'
  | 'thousandSep'
  | 'thousandSepNoDecimal'
  | 'date'
  | 'time'
  | 'email'
  | 'phone'
  | 'ssn'
  | 'mers'
  | 'creditScore'
  | 'color'
  | 'dollar'
  | 'percentage'

export interface IServicingInfoCustomField {
  key: string
  title: string
  inputType: CustomFieldInputType
  /** Sub-format for `inputType: 'Text'` or table format for `inputType: 'FileTable'`. */
  type?: CustomFieldTextFormat | string
  /** Options for `inputType: 'Select'`. Uses the SelectOptionInputs shape. */
  options?: ISelectOption[]
  span?: number | 'full'
  prefix?: string
  required?: boolean
  tooltip?: string
  /* ----- FileTable-only ----- */
  /** Allow multi-file selection. Defaults to true at runtime. */
  multiple?: boolean
  /** Show the file Status column. Defaults to false at runtime. */
  showStatus?: boolean
  /** Show the file Category column. Defaults to false at runtime. */
  showCategory?: boolean
}

export interface IServicingInfoCustomSection {
  id: string
  title: string
  tab: ServicingInfoTab
  fields: IServicingInfoCustomField[]
}

export interface IServicingInfoConfig {
  fields?: Record<string, IServicingInfoFieldConfig>
  sections?: Record<string, IServicingInfoSectionConfig>
  /** Display order of all section keys (built-in + custom). Missing keys keep their original order. */
  sectionOrder?: string[]
  customSections?: IServicingInfoCustomSection[]
}

export interface IServicingInfoSection {
  key: string
  title: string
  /** True if this is an admin-defined section (custom) that can be deleted. */
  isCustom: boolean
  /** Tab the section belongs to. */
  tab: ServicingInfoTab
  /** Field keys (in display order). The section header key itself is NOT included. */
  fieldKeys: string[]
}

/**
 * Decide which tab a built-in section key belongs to. Custom sections are
 * resolved by their stored `tab` value (see `parseSections`).
 */
export const getBuiltInSectionTab = (sectionKey: string): ServicingInfoTab => {
  if (INSURANCE_TAB_SECTION_KEYS.includes(sectionKey)) return 'Insurance'
  if (TAXES_TAB_SECTION_KEYS.includes(sectionKey)) return 'Taxes'
  if (PROPERTY_TAB_SECTION_KEYS.includes(sectionKey)) return 'Flood & Appraisal'
  return 'Loan Details'
}

/**
 * Decide which tab a field key belongs to, based on the section it sits under
 * (built-in tab assignment is hardcoded so this must agree with ServicingNote.tsx).
 */
export const getFieldTab = (
  fieldKey: string,
  inputs: Record<string, InputType>,
  insuranceInputs: Record<string, InputType>,
  taxInputs: Record<string, InputType>,
): ServicingInfoTab => {
  const insInputKeys = ['insuranceSection', ...Object.keys(insuranceInputs), 'insuranceReminderSection']
  const taxInputKeys = ['taxSection', ...Object.keys(taxInputs), 'taxReminderSection']
  if (insInputKeys.includes(fieldKey)) return 'Insurance'
  if (taxInputKeys.includes(fieldKey)) return 'Taxes'
  // Property sections and their fields are resolved by walking the section a
  // field sits under; see parseSections + getBuiltInSectionTab, which is the
  // authoritative mapping used at runtime.
  return getBuiltInSectionTab(fieldKey)
}

/**
 * Parse the flat inputs dictionary into a list of sections. Each section key
 * (inputType === 'section') marks the start of a section; following keys
 * (until the next section key) are its fields.
 *
 * The result is in the natural input order; reordering / overrides are applied
 * in `getOrderedSections`.
 */
export const parseSections = (inputs: Record<string, InputType>): IServicingInfoSection[] => {
  const sections: IServicingInfoSection[] = []
  let current: IServicingInfoSection | null = null

  Object.keys(inputs).forEach((key) => {
    const input = inputs[key] as any
    if (input?.inputType === 'section') {
      current = {
        key,
        title: input.title || '',
        isCustom: false,
        tab: getBuiltInSectionTab(key),
        fieldKeys: [],
      }
      sections.push(current)
    } else if (current) {
      current.fieldKeys.push(key)
    } else {
      // Fields appearing before any section header (rare): bucket them into a
      // synthetic header so the UI still renders them.
      current = {
        key: '__unsectioned__',
        title: 'Other',
        isCustom: false,
        tab: 'Loan Details',
        fieldKeys: [key],
      }
      sections.push(current)
    }
  })

  return sections
}

/**
 * Apply the admin config's `sectionOrder` to a list of sections. Sections not
 * listed in `sectionOrder` keep their original relative order and are appended
 * at the end of the tab they belong to.
 */
export const applySectionOrder = (
  sections: IServicingInfoSection[],
  sectionOrder: string[] = [],
): IServicingInfoSection[] => {
  if (!sectionOrder?.length) return sections

  const orderIndex = new Map<string, number>()
  sectionOrder.forEach((k, i) => orderIndex.set(k, i))

  const indexed = sections.map((s, i) => ({ s, i }))
  indexed.sort((a, b) => {
    const ai = orderIndex.has(a.s.key) ? (orderIndex.get(a.s.key) as number) : Number.MAX_SAFE_INTEGER
    const bi = orderIndex.has(b.s.key) ? (orderIndex.get(b.s.key) as number) : Number.MAX_SAFE_INTEGER
    if (ai !== bi) return ai - bi
    return a.i - b.i
  })

  return indexed.map((x) => x.s)
}

/**
 * Materialize custom sections (from the admin config) as IServicingInfoSection.
 * The synthetic section key uses the prefix `custom:` so it won't collide with
 * built-in section keys.
 */
export const buildCustomSections = (customSections: IServicingInfoCustomSection[] = []): IServicingInfoSection[] =>
  customSections.map((cs) => ({
    key: `custom:${cs.id}`,
    title: cs.title || 'Untitled',
    isCustom: true,
    tab: normalizeServicingInfoTab(cs.tab),
    fieldKeys: cs.fields.map((f) => f.key),
  }))

/**
 * Optional runtime context used by `customFieldToInput`. The admin page doesn't
 * have a loan number, but at runtime ServicingNote.tsx does — we use it to
 * compute the FileTable `filePath` and a sensible ActionButton `actionKey`.
 */
export interface CustomFieldRuntimeContext {
  loanNumber?: number | string | null
}

/**
 * Convert a stored custom field definition into a runtime `InputType` that
 * `RenderInput` accepts. Handles the same `type` shortcuts as the
 * LoanFieldsProperties VisiblePropertyModal (`dollar` → `thousandSep` + `$`,
 * `percentage` → `number` + `%`), and turns `ISelectOption[]` into the
 * key→title map that the runtime select expects.
 */
export const customFieldToInput = (field: IServicingInfoCustomField, ctx: CustomFieldRuntimeContext = {}): InputType => {
  const base: any = {
    title: field.title,
    span: field.span ?? 4,
    required: !!field.required,
  }
  if (field.tooltip) base.tooltip = field.tooltip

  let prefix = field.prefix
  let type = field.type

  if (type === 'dollar') {
    type = 'thousandSep'
    prefix = '$'
  } else if (type === 'percentage') {
    type = 'number'
    prefix = '%'
  }

  if (prefix) base.prefix = prefix

  // Normalize legacy / lowercased values so older stored configs still render.
  const normalizedType = String(field.inputType || 'Text').toLowerCase()

  switch (normalizedType) {
    case 'textarea':
      return { ...base, inputType: 'textarea' }
    case 'select': {
      // Support both the new `ISelectOption[]` shape and the legacy `string[]`.
      const raw = (field.options || []) as any[]
      const options: Record<string, string> = {}
      raw.forEach((opt) => {
        if (typeof opt === 'string') {
          options[opt] = opt
        } else if (opt && opt.visible !== false) {
          options[opt.key] = opt.title || opt.key
        }
      })
      return {
        ...base,
        inputType: 'select',
        options,
        hasDefaultOption: true,
      }
    }
    case 'check':
    case 'checkbox':
      return { ...base, inputType: 'checkbox' }
    case 'togglebutton':
      return { ...base, inputType: 'togglebutton' }
    case 'date':
      return { ...base, inputType: 'text', type: 'date' }
    case 'number':
      return { ...base, inputType: 'text', type: 'number' }
    case 'filetable': {
      // Stored as 'FileTable'; runtime expects lowercase 'filetable'.
      // Default the filePath bucket to a per-field folder under the loan; the
      // admin doesn't need to configure storage.
      const loanNumber = ctx.loanNumber
      const filePath = loanNumber
        ? `loan/${loanNumber}/servicing/custom/${field.key}`
        : `loan/servicing/custom/${field.key}`
      const tableFormat = field.type && field.type !== 'dollar' && field.type !== 'percentage' ? field.type : ''
      return {
        ...base,
        inputType: 'filetable',
        filePath,
        multiple: field.multiple !== false,
        showStatus: !!field.showStatus,
        showCategory: !!field.showCategory,
        acceptFileTypes: tableFormat === 'fannie34Xml' ? 'text/xml' : '',
      }
    }
    case 'text':
    default:
      return { ...base, inputType: 'text', type: type || 'text' }
  }
}
